import type {
  BrowserStreamEvent,
  EnvironmentId,
  OrchestrationShellSnapshot,
  OrchestrationShellStreamEvent,
  ServerConfig,
  ServerLifecycleWelcomePayload,
  TerminalEvent,
} from "@cmmd-forge/contracts";
import type { KnownEnvironment } from "@cmmd-forge/client-runtime";

import type { WsRpcClient } from "~/rpc/wsRpcClient";
import {
  openWorkspaceReadinessStream,
  type WorkspaceReadinessClientHandle,
} from "~/rpc/workspaceReadinessClient";

export interface EnvironmentConnection {
  readonly kind: "primary" | "saved" | "desktop-local";
  readonly environmentId: EnvironmentId;
  readonly knownEnvironment: KnownEnvironment;
  readonly client: WsRpcClient;
  readonly ensureBootstrapped: () => Promise<void>;
  readonly reconnect: () => Promise<void>;
  readonly ensureProviderAuthority?: () => Promise<void>;
  readonly dispose: () => Promise<void>;
}

interface OrchestrationHandlers {
  readonly applyShellEvent: (
    event: OrchestrationShellStreamEvent,
    environmentId: EnvironmentId,
  ) => void;
  readonly syncShellSnapshot: (
    snapshot: OrchestrationShellSnapshot,
    environmentId: EnvironmentId,
  ) => void;
  readonly applyTerminalEvent: (event: TerminalEvent, environmentId: EnvironmentId) => void;
  readonly applyBrowserEvent: (event: BrowserStreamEvent, environmentId: EnvironmentId) => void;
}

interface EnvironmentConnectionInput extends OrchestrationHandlers {
  readonly kind: EnvironmentConnection["kind"];
  readonly knownEnvironment: KnownEnvironment;
  readonly client: WsRpcClient;
  readonly resolveEnvironmentId?: () => EnvironmentId | null | undefined;
  readonly readiness?: {
    readonly bearerToken?: string;
    readonly openStream?: (input: {
      readonly url: string;
      readonly bearerToken?: string;
      readonly environmentId: EnvironmentId;
    }) => WorkspaceReadinessClientHandle;
  };
  readonly refreshMetadata?: () => Promise<void>;
  readonly ensureProviderAuthority?: () => Promise<void>;
  readonly onDispose?: () => void;
  readonly onConfigSnapshot?: (config: ServerConfig) => void;
  readonly onWelcome?: (
    payload: ServerLifecycleWelcomePayload,
    environmentId: EnvironmentId,
  ) => void;
  // FINDING-14: invoked when the shell subscription terminates with a
  // non-transport error (e.g. a startup snapshot failure wrapped as
  // OrchestrationGetSnapshotError). transport.subscribe does not retry that
  // class, so without surfacing it the shell stream dies silently. Called only
  // for the mid-session resubscribe case (no caller is awaiting bootstrap);
  // the initial-bootstrap case is routed through bootstrapGate.reject instead.
  readonly onShellSubscriptionError?: (error: unknown, environmentId: EnvironmentId) => void;
  readonly onIdentityMismatch?: (error: Error) => void;
  readonly bootstrapTimeoutMs?: number;
  // Proxied cloud runtimes (cmmd-runtime:<id>) reach the VM through the CMMD
  // router, and the VM reports its OWN local environment id in the lifecycle
  // welcome — which never equals the synthetic cmmd-runtime:<id> key the client
  // connects with. The routeKey + CMMD-signed runtimeTicket (scoped to
  // runtime_id + machine_id) already bind this socket to the correct runtime,
  // so the strict identity guard would tear down EVERY healthy cloud connect
  // mid-bootstrap ("All fibers interrupted"). When true, accept the first
  // reported identity and only treat a later CHANGE as a real reroute.
  readonly tolerateReportedEnvironmentIdentity?: boolean;
}

const DEFAULT_ENVIRONMENT_BOOTSTRAP_TIMEOUT_MS = 60_000;

function resolveEnvironmentReadinessUrl(environment: KnownEnvironment): string {
  const url = new URL(environment.target.httpBaseUrl);
  const basePath = url.pathname.endsWith("/") ? url.pathname.slice(0, -1) : url.pathname;
  url.pathname = `${basePath}/api/workspace/readiness`;
  url.search = "";
  url.hash = "";
  return url.toString();
}

// FINDING-13/14 bootstrap gate. Each `wait()` registers its own deferred in a
// shared `Set`; `resolve()`/`reject()` drain every registered waiter. `reset()`
// (fired on every WS reconnect via onResubscribe) only flips the gate back to
// "closed" and clears the latched error — it deliberately LEAVES live waiters
// registered so they settle on the next snapshot/error instead of being
// abandoned on an orphaned promise. This is the waiter-tracking realization of
// the generation-counter fix: a healthy reconnect produces zero spurious
// rejections (FINDING-13), and there is never an orphan promise to leak as an
// unhandled rejection. `latchedError` covers the rare ordering where an error
// settles the gate before any caller has registered a waiter, so a later
// `wait()` rejects immediately instead of hanging to the timeout (FINDING-14).
interface BootstrapWaiter {
  readonly resolve: () => void;
  readonly reject: (error: unknown) => void;
}

function createBootstrapGate(input: {
  readonly environmentId: EnvironmentId;
  readonly label: string;
  readonly timeoutMs: number;
}) {
  const waiters = new Set<BootstrapWaiter>();
  let latchedError: unknown = null;
  let hasLatchedError = false;
  // Latch a resolution that lands before any caller has registered a waiter so a
  // subsequent `wait()` in the same open window resolves immediately instead of
  // hanging (e.g. the reconnect path resolves the gate via an early snapshot
  // before its own `await bootstrapGate.wait()` runs). `reset()` re-closes the
  // gate, so a `wait()` after a reconnect still waits for a fresh snapshot.
  let isResolved = false;

  return {
    hasWaiters: () => waiters.size > 0,
    wait: () => {
      if (hasLatchedError) {
        return Promise.reject(latchedError);
      }
      if (isResolved) {
        return Promise.resolve();
      }
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      let waiter: BootstrapWaiter | null = null;
      const settled = new Promise<void>((resolve, reject) => {
        waiter = { resolve, reject };
        waiters.add(waiter);
      });
      const timeout = new Promise<never>((_, reject) => {
        timeoutId = globalThis.setTimeout(() => {
          reject(
            new Error(
              `Timed out while waiting for ${input.label} (${input.environmentId}) to stream its shell snapshot.`,
            ),
          );
        }, input.timeoutMs);
      });
      return Promise.race([settled, timeout]).finally(() => {
        if (timeoutId !== null) {
          globalThis.clearTimeout(timeoutId);
        }
        if (waiter !== null) {
          waiters.delete(waiter);
        }
      });
    },
    resolve: () => {
      hasLatchedError = false;
      latchedError = null;
      isResolved = true;
      const pending = [...waiters];
      waiters.clear();
      for (const waiter of pending) {
        waiter.resolve();
      }
    },
    reject: (error: unknown) => {
      hasLatchedError = true;
      latchedError = error;
      isResolved = false;
      const pending = [...waiters];
      waiters.clear();
      for (const waiter of pending) {
        waiter.reject(error);
      }
    },
    reset: () => {
      // Re-close the gate for the next snapshot. Live waiters stay registered so
      // they re-follow the fresh stream rather than hanging on an abandoned
      // promise; clearing both latches makes a `wait()` after a reconnect await
      // a fresh snapshot (and lets a healthy reconnect resolve cleanly).
      hasLatchedError = false;
      latchedError = null;
      isResolved = false;
    },
  };
}

export function createEnvironmentConnection(
  input: EnvironmentConnectionInput,
): EnvironmentConnection {
  const environmentId = input.knownEnvironment.environmentId;

  if (!environmentId) {
    throw new Error(
      `Known environment ${input.knownEnvironment.label} is missing its environmentId.`,
    );
  }

  let disposed = false;
  let identityMismatchHandled = false;
  // The latest full config this connection has observed, so a later
  // `providerStatuses` push (no `environment`/full-config fields of its own)
  // can be merged into something instead of being dropped. See unsubConfig.
  let latestConfig: ServerConfig | null = null;
  // For proxied cloud runtimes: the first reported runtime identity we accept.
  // A later welcome/config reporting a DIFFERENT id is a real reroute.
  let latchedReportedEnvironmentId: EnvironmentId | null = null;
  const bootstrapGate = createBootstrapGate({
    environmentId,
    label: input.knownEnvironment.label,
    timeoutMs: input.bootstrapTimeoutMs ?? DEFAULT_ENVIRONMENT_BOOTSTRAP_TIMEOUT_MS,
  });
  const readinessStream = input.readiness
    ? (input.readiness.openStream ?? openWorkspaceReadinessStream)({
        url: resolveEnvironmentReadinessUrl(input.knownEnvironment),
        environmentId,
        ...(input.readiness.bearerToken ? { bearerToken: input.readiness.bearerToken } : {}),
      })
    : null;

  // Returns false when a mismatch is detected and the caller should bail out
  // without delivering the mismatched payload (e.g. onWelcome / onConfigSnapshot).
  // On first mismatch: tears down the connection and notifies the caller via the
  // optional onIdentityMismatch hook. Subsequent mismatches are silent no-ops
  // because cleanup has already run (idempotent via identityMismatchHandled).
  const handleIdentityMismatch = (message: string): boolean => {
    if (!identityMismatchHandled) {
      identityMismatchHandled = true;
      const error = new Error(message);
      cleanup();
      void input.client.dispose();
      input.onIdentityMismatch?.(error);
    }
    return false;
  };

  const observeEnvironmentIdentity = (
    nextEnvironmentId: EnvironmentId,
    source: string,
  ): boolean => {
    if (input.tolerateReportedEnvironmentIdentity) {
      // Proxied cloud runtime: the VM reports its own local id, not the
      // cmmd-runtime:<id> key. Trust the router/ticket binding — accept the
      // first reported identity; only a CHANGE to a different id is a reroute.
      if (latchedReportedEnvironmentId === null) {
        latchedReportedEnvironmentId = nextEnvironmentId;
        return true;
      }
      if (latchedReportedEnvironmentId === nextEnvironmentId) {
        return true;
      }
      return handleIdentityMismatch(
        `Environment connection ${environmentId} reported a changed runtime identity (${latchedReportedEnvironmentId} -> ${nextEnvironmentId}) via ${source}.`,
      );
    }
    const activeEnvironmentId = input.resolveEnvironmentId?.() ?? environmentId;
    if (activeEnvironmentId !== nextEnvironmentId) {
      return handleIdentityMismatch(
        `Environment connection ${activeEnvironmentId} changed identity to ${nextEnvironmentId} via ${source}.`,
      );
    }
    return true;
  };

  const currentEnvironmentId = () => input.resolveEnvironmentId?.() ?? environmentId;

  const unsubLifecycle = input.client.server.subscribeLifecycle(
    (event: Parameters<Parameters<WsRpcClient["server"]["subscribeLifecycle"]>[0]>[0]) => {
      if (event.type !== "welcome") {
        return;
      }
      if (
        !observeEnvironmentIdentity(
          event.payload.environment.environmentId,
          "server lifecycle welcome",
        )
      ) {
        return;
      }
      input.onWelcome?.(event.payload, currentEnvironmentId());
    },
  );

  const unsubConfig = input.client.server.subscribeConfig(
    (event: Parameters<Parameters<WsRpcClient["server"]["subscribeConfig"]>[0]>[0]) => {
      if (event.type === "snapshot") {
        if (
          !observeEnvironmentIdentity(
            event.config.environment.environmentId,
            "server config snapshot",
          )
        ) {
          return;
        }
        latestConfig = event.config;
        input.onConfigSnapshot?.(event.config);
        return;
      }

      if (event.type !== "providerStatuses") {
        // keybindingsUpdated / settingsUpdated / environmentUpdated: no
        // saved/cloud-environment consumer needs these today (the primary
        // environment gets full parity via rpc/serverState.ts). Extend this
        // branch the same way if one starts needing them.
        return;
      }

      // A provider registry refresh completing (e.g. a freshly-booted cloud
      // sprite finishing model-catalog discovery) arrives as a live
      // `providerStatuses` push on this SAME subscription, not another
      // `snapshot`. Without merging it in, a saved/cloud connection's config
      // stays frozen at whatever the connect-time snapshot reported — usually
      // an empty provider list, since discovery is still in flight — for the
      // rest of the connection's life. Merging it here is what lets a caller
      // that reads serverConfig.providers (e.g. the /new submission flow's
      // readiness wait) ever observe the real, later readiness signal instead
      // of a stale snapshot that a full reconnect happened to mask.
      if (!latestConfig) {
        return;
      }
      const mergedConfig: ServerConfig = {
        ...latestConfig,
        providers: event.payload.providers,
      };
      latestConfig = mergedConfig;
      input.onConfigSnapshot?.(mergedConfig);
    },
  );

  const unsubShell = input.client.orchestration.subscribeShell(
    (item: Parameters<Parameters<WsRpcClient["orchestration"]["subscribeShell"]>[0]>[0]) => {
      if (item.kind === "snapshot") {
        input.syncShellSnapshot(item.snapshot, currentEnvironmentId());
        bootstrapGate.resolve();
        return;
      }
      input.applyShellEvent(item, currentEnvironmentId());
    },
    {
      onResubscribe: () => {
        if (disposed) {
          return;
        }
        bootstrapGate.reset();
      },
      // FINDING-14: the shell stream terminated with a non-transport error.
      // transport.subscribe does not retry this class, so the stream is now
      // permanently dead. If a caller is awaiting bootstrap, reject its gate so
      // the error surfaces immediately (and the CMMD-runtime bootstrap retry
      // loop in service.ts can re-classify and retry) instead of stalling until
      // the 60s timeout. Otherwise (mid-session resubscribe with no waiter)
      // surface a recoverable runtime error without rejecting an unobserved
      // gate promise, which would become an unhandled rejection.
      onError: (error: unknown) => {
        if (disposed) {
          return;
        }
        if (bootstrapGate.hasWaiters()) {
          bootstrapGate.reject(error);
          return;
        }
        input.onShellSubscriptionError?.(error, currentEnvironmentId());
      },
    },
  );

  const unsubTerminalEvent = input.client.terminal.onEvent(
    (event: Parameters<Parameters<WsRpcClient["terminal"]["onEvent"]>[0]>[0]) => {
      input.applyTerminalEvent(event, currentEnvironmentId());
    },
  );

  const unsubBrowserEvent = input.client.browser?.onEvent({}, (event) => {
    input.applyBrowserEvent(event, currentEnvironmentId());
  });

  // FINDING-CB1 readiness phase stream — post-upgrade. Pairs with the
  // pre-upgrade SSE channel (`/api/workspace/readiness`) in
  // ~/rpc/workspaceReadinessClient.ts; both write to workspaceReadinessAtom
  // with monotonic-ts merge so the overlay sees a coherent transition
  // across the SSE-to-WS handover. Old servers return `RpcNotFound` here,
  // the subscription cleanly no-ops, and the SPA falls back to the
  // time-derived overlay copy.
  const unsubWorkspaceReadiness = input.client.server.subscribeWorkspaceReadiness((event) => {
    if (disposed) return;
    // Lazy import to keep the runtime/connection bundle from depending
    // on the readiness atom directly (atom lives in ~/state, this file
    // is in ~/environments/runtime). The import resolves once.
    void import("~/state/workspaceReadinessAtom").then(
      ({ setWorkspaceReadinessPhaseForEnvironment }) => {
        if (disposed) return;
        setWorkspaceReadinessPhaseForEnvironment(currentEnvironmentId(), event);
      },
    );
  });

  const cleanup = () => {
    if (disposed) return;
    disposed = true;
    input.onDispose?.();
    readinessStream?.close();
    unsubShell();
    unsubTerminalEvent();
    unsubBrowserEvent?.();
    unsubLifecycle();
    unsubConfig();
    unsubWorkspaceReadiness();
  };

  return {
    kind: input.kind,
    environmentId,
    knownEnvironment: input.knownEnvironment,
    client: input.client,
    ensureBootstrapped: () => bootstrapGate.wait(),
    reconnect: async () => {
      bootstrapGate.reset();
      try {
        await input.client.reconnect();
        await input.refreshMetadata?.();
        await bootstrapGate.wait();
      } catch (error) {
        bootstrapGate.reject(error);
        throw error;
      }
    },
    ...(input.ensureProviderAuthority
      ? { ensureProviderAuthority: input.ensureProviderAuthority }
      : {}),
    dispose: async () => {
      cleanup();
      await input.client.dispose();
    },
  };
}
